import { useCallback, useEffect, useState } from "react";
import type { AgencyLaunchCertificateViewV1 } from "@enrein/shared-contracts/contracts/launch-certificate";
import { ineligibilityReasonLabel } from "@enrein/shared-contracts";
import type { StudioApiClient } from "./api.js";

export function LaunchCertificatePanel({
  api,
  projectId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string | null;
}) {
  const [view, setView] = useState<AgencyLaunchCertificateViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    try {
      setView(await api.getCurrentLaunchCertificate(projectId));
    } catch (err) {
      setView(null);
      const message = err instanceof Error ? err.message : String(err);
      if (!message.includes("launch_certificate_not_found")) {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openHtml = async () => {
    if (!projectId) return;
    try {
      const html = await api.getLaunchCertificateHtml(projectId);
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!projectId) {
    return <p className="empty-state">Select a project to view Launch Certificate.</p>;
  }
  if (busy && !view) return <p className="loading-panel">Loading Launch Certificate…</p>;
  if (error) return <p className="error">{error}</p>;
  if (!view) {
    return (
      <p className="empty-state">
        No Launch Certificate yet. Export a Departure Kit with eligible Launch Proof first.
      </p>
    );
  }

  if (view.status === "INELIGIBLE") {
    return (
      <div className="report-card proof-card">
        <p className="eyebrow">Launch Certificate</p>
        <h3>Not issued</h3>
        <p>
          {view.ineligibilityReason
            ? ineligibilityReasonLabel(view.ineligibilityReason)
            : "Launch Proof is not eligible for certificate packaging."}
        </p>
        <p className="muted">Review Launch Proof above for blocking issues.</p>
        <button type="button" className="secondary" onClick={() => void load()}>
          Refresh
        </button>
      </div>
    );
  }

  const cert = view.certificate;
  if (!cert) return null;

  return (
    <div className="report-card proof-card">
      <p className="eyebrow">Launch Certificate</p>
      <h3>{cert.title}</h3>
      <p>{cert.headline}</p>
      {cert.warningBanner ? <p className="warning-banner">{cert.warningBanner}</p> : null}
      <dl>
        <dt>Business</dt>
        <dd>{cert.businessName}</dd>
        <dt>Status</dt>
        <dd>{cert.aggregateStatusLabel}</dd>
        <dt>Issued</dt>
        <dd>{cert.issuedAt}</dd>
      </dl>
      {view.verificationCode ? (
        <p>
          <strong>Verification code:</strong> {view.verificationCode}
        </p>
      ) : null}
      {view.publicVerifyPath ? (
        <p className="muted">Public verify path: {view.publicVerifyPath}</p>
      ) : null}
      <div className="button-row">
        <button type="button" onClick={() => void openHtml()}>
          Open printable HTML
        </button>
        <button type="button" className="secondary" onClick={() => void load()}>
          Refresh
        </button>
      </div>
      <details>
        <summary>Disclaimers</summary>
        <ul>
          {cert.disclaimers.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
